import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AppClient } from "../services/client.service";
import {
  ISendNotificationRequest,
  SendNotificationRequest,
} from "../generated/api-client";

function parseRecipients(value: string): string[] {
  return value
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean);
}

export default function MessagesNewPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const [recipients, setRecipients] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const recipientsList = parseRecipients(recipients);

  const recipientsError =
    recipientsList.length === 0
      ? intl.formatMessage({ id: "validationRecipientsRequired" })
      : recipientsList.length > 100
        ? intl.formatMessage({ id: "validationRecipientsMax" })
        : null;

  const titleError =
    title.trim().length === 0
      ? intl.formatMessage({ id: "validationTitleRequired" })
      : null;

  const bodyError =
    body.trim().length === 0
      ? intl.formatMessage({ id: "validationBodyRequired" })
      : body.length > 2000
        ? intl.formatMessage({ id: "validationBodyMaxLength" })
        : null;

  const canSubmit = !recipientsError && !titleError && !bodyError;

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const request: ISendNotificationRequest = {
        title: title.trim(),
        body: body.trim(),
        emailRecipients: recipientsList,
      };
      await AppClient.getInstance().notifications(
        AppClient.version,
        request as SendNotificationRequest
      );
      navigate("/messages");
    } catch (_e: any) {
      // Error notifications are handled globally by axios interceptors.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Stack spacing={2} sx={{ p: 2 }}>
        <Box>
          <Breadcrumbs aria-label="breadcrumb">
            <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
              {intl.formatMessage({ id: "dashboard" })}
            </Link>
            <Link
              to="/messages"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              {intl.formatMessage({ id: "messages" })}
            </Link>
            <Typography color="text.primary">
              {intl.formatMessage({ id: "sendMessage" })}
            </Typography>
          </Breadcrumbs>
          <Typography variant="h4">
            {intl.formatMessage({ id: "sendMessage" })}
          </Typography>
        </Box>

        <Box sx={{ width: "100%" }}>
          <Stack>
            <TextField
              label={intl.formatMessage({ id: "messageRecipients" })}
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              helperText={
                submitAttempted
                  ? recipientsError || " "
                  : intl.formatMessage({ id: "messageRecipientsHelper" })
              }
              error={submitAttempted && !!recipientsError}
              fullWidth
              required
            />
            <TextField
              sx={{ mt: 4 }}
              label={intl.formatMessage({ id: "messageTitle" })}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              helperText={submitAttempted ? titleError || " " : " "}
              error={submitAttempted && !!titleError}
              fullWidth
              required
            />
            <TextField
              label={intl.formatMessage({ id: "messageBody" })}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              helperText={
                submitAttempted
                  ? bodyError || " "
                  : intl.formatMessage(
                      { id: "messageBodyHelper" },
                      { max: 2000 }
                    )
              }
              error={submitAttempted && !!bodyError}
              multiline
              minRows={5}
              fullWidth
              required
            />
            <Box sx={{ display: "flex", justifyContent: "right" }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting}
                startIcon={
                  submitting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
              >
                {intl.formatMessage({ id: "sendMessage" })}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
}
